import { isValidationSample } from '../lib/validation-sample.js';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ApiRequestError,
  approveItem,
  approveItemWithEdit,
  bulkApproveGroups,
  bulkCancelItems,
  bulkRestoreItems,
  cancelItem,
  commentItem,
  restoreItem,
  fetchWorkItem,
  fetchCourses,
  fetchGroups,
  fetchQueueItems,
  fetchQueueStats,
  fetchReleaseWaves,
  fetchWorkItemHistory,
  sourceDocumentHref,
  type CourseSummary,
  type GroupNode,
} from '../api/client.js';
import { useItemOpenedSignal } from '../activity/useItemOpenedSignal.js';
import { EvidenceGate } from './EvidenceGate.js';
import { isMachineActor, MachineActorBadge, MachineAuthorityLine } from './MachineActorBadge.js';
import { AppendPreview } from './AppendPreview.js';
import { PatchPreview } from './PatchPreview.js';
import { PrerequisiteEditor } from './PrerequisiteEditor.js';
import { EditsEnvelopeEditor } from './EditsEnvelopeEditor.js';
import { isPureEditsEnvelope } from '../lib/editsEnvelopeEdit.js';
import { describeApiError } from '../lib/apiErrorMessage.js';
import { isPrerequisiteOnlyPatch } from '../lib/prerequisitePatch.js';
import { StepWiringPane } from './StepWiringPane.js';
import { QueueItemsContext } from './NewLessonQuestionLink.js';
import { WorkItemTitle } from './WorkItemTitle.js';
import { PracticeItemMetadata } from './PracticeItemMetadata.js';
import {
  compareClusters,
  deriveClusterMeta,
  isPrereqRewirePatch,
  workItemTitle as itemTitle,
} from '../lib/clusterMeta.js';
import {
  BULK_APPROVE_HUMAN_ITEM_LIMIT,
  atomicGroupSize,
  describeWithheldGroup,
  isInMultiMemberGroup,
  planClusterApproval,
  rosterMembersToResolve,
} from '../lib/atomicGroups.js';
import { describeSources } from '../lib/sources.js';
import { describeCommentDeliveryFor } from '../lib/commentDelivery.js';
import { deriveReviewerBrief } from '../lib/reviewerBrief.js';
import { resolveStoredRatings } from '../lib/ratingConfig.js';
import {
  BUTTON_INTERACTION_STYLES,
  LINK_INTERACTION_STYLES,
  PRIMARY_ACTION_LINK_STYLES,
} from '../lib/actionStyles.js';
import type {
  BulkApproveFailure,
  Priority,
  QueueFilters,
  QueueStats,
  ReleaseWaveSummary,
  ReviewEventEntry,
  SectionStat,
  TaskType,
  WorkItemStatus,
  WorkItemSummary,
} from '../types/workflow.js';
import type { BulkApproveOutcome } from '../types/bulk-approve.js';
import { resolveCourseSelection } from '../lib/courseSelection.js';
import { readCourseParam } from '../lib/courseParam.js';
import {
  evidenceGateApplies,
  evidenceReadiness,
} from '../lib/evidenceReadiness.js';
import { buildApproveNotes } from '../lib/followUpTags.js';
import {
  canConfirmRejection,
  findRejectionReason,
  getRejectionReasons,
  rejectionItemKind,
  rejectionNoteRequired,
} from '../lib/rejectionReasons.js';
import {
  buildCancelNotes,
  isLessonDraft,
  isNoSecondTry,
  isStepAppend,
  isPracticeItem,
  lessonReviewPath,
  lessonTranscriptPath,
  practiceItemAttachment,
  practiceReviewPath,
  statusBadgeClass, isPlacedLesson } from '../lib/workItems.js';

/**
 * The actions whose `notes` a human wrote or composed, and are therefore worth reading back on the
 * item. `rating`, `annotate` and `annotation_resolve` carry structured payloads with their own
 * surfaces, not prose.
 *
 * This list used to be `comment` alone, which made the follow-up tag row (§4.3) lie: it promises
 * "Recorded on the item and will be handled later", and the tags rode an approve's `notes` into
 * storage where **no screen displayed them** — the audit table truncates them behind the status
 * columns and the daily digest clips at 70 characters, nine short of the acknowledgement sentence
 * that precedes them. This list is the one place to widen if another action starts carrying prose.
 */
const NOTED_ACTIONS = ['comment', 'approve', 'approve_with_edit', 'cancel', 'restore'] as const;

/** What a non-comment note is labelled as, so an approval never reads as something someone typed. */
const NOTED_ACTION_LABELS: Record<Exclude<(typeof NOTED_ACTIONS)[number], 'comment'>, string> = {
  approve: 'Approved',
  approve_with_edit: 'Approved with edit',
  cancel: 'Rejected',
  restore: 'Restored',
};

type ReviewNoteEvent = Extract<ReviewEventEntry, { action: (typeof NOTED_ACTIONS)[number] }> & { notes?: string };

function isReviewNoteEvent(event: ReviewEventEntry): event is ReviewNoteEvent {
  if (!(NOTED_ACTIONS as readonly string[]).includes(event.action)) {
    return false;
  }
  // A MACHINE decision always renders, note or not. A bulk run can approve hundreds of items
  // without typing anything, and the note-less filter below would then hide the entire run from
  // the one screen that shows item history — leaving the trail true but invisible.
  if (isMachineActor(event)) {
    return true;
  }
  // An ordinary human approve carries no notes at all; an empty card would be worse than no card.
  return (
    typeof (event as { notes?: unknown }).notes === 'string'
    && (event as { notes: string }).notes.trim().length > 0
  );
}

const BANNER_TONE_STYLES = {
  error: 'border-rose-200 bg-rose-50 text-rose-800',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
} as const;

type DashboardBanner = {
  tone: 'error' | 'success' | 'warning';
  message: string;
};

type DashboardFilters = QueueFilters & {
  taskType?: TaskType;
  search: string;
};

type SectionEntry = {
  sectionId: string;
  label: string;
  count: number;
  icon: string;
};

type FilterOption = {
  value: string;
  label: string;
};

// Section names resolved from the course structure, tagged with the course they belong to so a
// slow response for a previous course can never label the current one.
type SectionTitleIndex = {
  courseKey: string;
  titles: Map<string, string>;
  settled: boolean;
};

const EMPTY_COURSES: CourseSummary[] = [];
const EMPTY_SECTION_TITLES: SectionTitleIndex = { courseKey: '', titles: new Map(), settled: true };

const SECTION_ICONS: Record<string, string> = {
  economics: 'trending_up',
  equities: 'show_chart',
  ethics: 'verified_user',
  'fixed income': 'account_balance',
  'portfolio management': 'pie_chart',
  'quantitative methods': 'functions',
};

/**
 * A REMOUNT ON COURSE CHANGE, and it is load-bearing rather than tidy.
 *
 * This screen's filters are one-way: {@link parseFiltersFromUrl} runs once, in a `useState`
 * initialiser, and `syncUrl` then writes state OUT to the address bar. Nothing reads the course back
 * IN. That was harmless while the only way to change course was to load the page — and became a live
 * defect the moment the header grew a level control that navigates: measured in a real browser at
 * `http://127.0.0.1:4173` on 2026-08-28, switching to CFA Level III left the address bar and the
 * header saying Level III while six Level I cluster cards, an "All 21" count and live approve
 * buttons stayed on screen.
 *
 * Remounting discards every piece of course-scoped state at once — items, stats, section titles,
 * waves, the expanded row, the action banner — instead of asking each of them to notice separately.
 * Same pattern, same reason, as `KeyedMicroLessonReview`.
 *
 * The key is the course the URL NAMES, not the resolved one: resolution needs the catalog, which
 * arrives later, and keying on a value that changes as data loads would remount the screen
 * mid-load. An unresolved key still changes exactly when the course in the URL changes, which is
 * the event this exists to catch.
 */
export function KeyedWorkQueueDashboard({ courses = EMPTY_COURSES }: { courses?: CourseSummary[] }) {
  const [searchParams] = useSearchParams();
  return <WorkQueueDashboard courses={courses} key={readCourseParam(searchParams) ?? ''} />;
}

export function WorkQueueDashboard({ courses = EMPTY_COURSES }: { courses?: CourseSummary[] }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<WorkItemSummary[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [availableCourses, setAvailableCourses] = useState<CourseSummary[]>(courses);
  // Whether the course catalog has ANSWERED — see the load effect below. Gates the first queue
  // read, because until it answers there is no way to tell an authorised course from any other.
  const [catalogSettled, setCatalogSettled] = useState(courses.length > 0);
  /**
   * Whether the course in `filters` has been CHECKED against this session's grants.
   *
   * Distinct from {@link catalogSettled}, and both are needed. `catalogSettled` alone is not enough:
   * the catalog arriving and the filters being corrected are two state updates in the same batch, so
   * an effect gated on `catalogSettled` still fires in that commit holding the *uncorrected* filters
   * and issues one request for the course the URL named. This flag is set in the same effect that
   * corrects the filters, so both land in the same following render and the first request the queue
   * ever makes names a course this session is allowed to open.
   */
  const [courseReconciled, setCourseReconciled] = useState(courses.length > 0);
  const [waves, setWaves] = useState<ReleaseWaveSummary[]>([]);
  // sectionId is the top-level Brain2 group id, so the course structure is the authoritative
  // source of section NAMES. Carries the courseKey it was loaded for, and a `settled` flag: the
  // rows are sorted by label, so rendering them before the titles arrive would re-sort them under
  // the reviewer's cursor. `settled` is set on failure too, so a failed lookup still renders
  // (with fallback labels) rather than hiding the tree forever.
  const [sectionTitles, setSectionTitles] = useState<SectionTitleIndex>(EMPTY_SECTION_TITLES);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // **Expanding a row IS opening the item, and on this screen it is the only way to read one.**
  // A `fix` item is reviewed inline here — it never reaches `/queue/lesson/:id` or
  // `/queue/practice/:id`, where the other two call sites live — so without this line the reading
  // signal is blind to the whole `fix` population, which is what the production table showed for
  // five weeks: 477 decisions, zero `item_opened` rows.
  useItemOpenedSignal(expandedId ?? undefined);
  const [bannerVisible, setBannerVisible] = useState(searchParams.get('imported') === 'true');
  const [actionBanner, setActionBanner] = useState<DashboardBanner | null>(null);
  const [filters, setFilters] = useState<DashboardFilters>(() => parseFiltersFromUrl(searchParams, courses));
  // Every filter change starts a queue load, and the responses can come back in any order. The
  // LAST REQUEST wins, never the last response: otherwise a slower earlier request overwrites the
  // list after the reviewer has already moved on, and they act on items the sidebar, the header and
  // the URL all say they are not looking at — approving the wrong content with no visible symptom.
  const queueRequestRef = useRef(0);
  const queueAbortRef = useRef<AbortController | null>(null);
  // A mutation handler suspends across its own await, so when it resumes its closure still holds
  // the filters that were selected when the reviewer clicked. Its recovery refresh must load the
  // section they are looking at NOW — otherwise it becomes the newest request and the guard above
  // faithfully protects the wrong section. Declared before every effect that reads it so the
  // mirror is already current when they run.
  const filtersRef = useRef(filters);
  // Bumped whenever a completed mutation is reflected locally. A read whose flight spans a bump
  // was answered from pre-mutation state, so applying it would undo a decision the reviewer has
  // already made (and the server has already accepted).
  const mutationEpochRef = useRef(0);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    setAvailableCourses(courses);
  }, [courses]);

  // Nothing in flight should outlive the dashboard, and nothing still resolving owns its state.
  useEffect(
    () => () => {
      queueRequestRef.current += 1;
      queueAbortRef.current?.abort();
    },
    [],
  );

  useEffect(() => {
    if (courses.length > 0) {
      setCatalogSettled(true);
      return;
    }

    let cancelled = false;
    void fetchCourses()
      .then((loadedCourses) => {
        if (!cancelled) {
          setAvailableCourses(loadedCourses);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableCourses([]);
        }
      })
      // Settled means ANSWERED, success or failure — never "succeeded". A catalog we could not read
      // must not freeze the queue forever; it costs the grant check, not the screen.
      .finally(() => {
        if (!cancelled) {
          setCatalogSettled(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [courses]);

  /**
   * Reconcile the course the URL asked for against the catalog, once the catalog exists.
   *
   * This is where the deferred grant check in {@link parseFiltersFromUrl} is actually taken. It
   * REPLACES a course this session holds no grant for and FILLS an empty one — but it leaves an
   * authorised, explicitly requested course alone. It used to only fill an empty slot, which,
   * combined with the course being dropped on first render, silently substituted the preferred
   * course for every course that was not already the preferred one.
   */
  useEffect(() => {
    if (!catalogSettled) {
      return;
    }

    setFilters((current) => {
      // An unreadable catalog costs the grant check, not the screen: keep what the URL asked for and
      // let the server be the authority. Failing the other way would blank the reviewer's queue
      // because a list of course titles could not be fetched.
      if (availableCourses.length === 0) {
        return current;
      }
      // `resolveCourseSelection`, not the bare default: when the URL named no course this is where
      // the level the reviewer last chose in this tab is honoured. It still discards a course the
      // session holds no grant for, in either branch, because both resolve through the grant list.
      const { courseKey: resolved } = resolveCourseSelection(availableCourses, current.courseKey ?? null);
      if (!resolved || resolved === current.courseKey) {
        return current;
      }
      return { ...current, courseKey: resolved };
    });
    setCourseReconciled(true);
  }, [availableCourses, catalogSettled]);

  /**
   * THE ADDRESS BAR IS WRITTEN IN THE SAME COMMIT AS THE FILTERS, NOT ONE FLUSH LATER.
   *
   * `useLayoutEffect`, and the difference is a defect rather than a preference — the same "an effect
   * is one frame late" failure the structural badge had, in its other direction. A *passive* effect
   * is queued at commit and flushed after React yields, so between the commit and the write there is
   * a window in which the reviewer can act. He does exactly that, and the write lands on top of him:
   *
   * 1. A link names course `350`, which this session cannot open. The queue mounts, reconciles it to
   *    `12b`, and writes that back — which re-keys {@link KeyedWorkQueueDashboard} and mounts a fresh
   *    instance for `12b`. That instance queues a sync of its own, because its filters just came from
   *    the URL and every mount normalises the address bar.
   * 2. Before that queued write flushes, the reviewer picks **Level III** from the header — a `PUSH`
   *    to `?courseKey=400`.
   * 3. The queued write then flushes and REPLACES the address bar with `courseKey=12b`, because it
   *    was built from a render that predates the reviewer's choice.
   *
   * The reviewer chose Level III and the screen put him back on Level I, silently. Observed as an
   * intermittent failure of the level-choice test on a loaded machine (2026-08-29), traced to the
   * write above with `urlWas=12b` landing after the `PUSH` and no `courseKey=400` write ever
   * following it. `gpt-5.6-sol` predicted a race here on 2026-08-28 from reading the code; the
   * sequence it described was a *stale* instance writing, and the instrumented one is a *fresh*
   * instance's mount-time normalisation — same window, different occupant.
   *
   * A layout effect runs synchronously inside the commit, so there is no window: the write either
   * happens before the reviewer's navigation (and his `PUSH` lands on top of it, which is what
   * should happen) or the instance is re-keyed away before it ever commits, and never writes at all.
   *
   * This is a URL normalisation, never a data read — nothing here is fetched from a layout effect.
   */
  useLayoutEffect(() => {
    syncUrl(filters);
  }, [filters]);

  useEffect(() => {
    setBannerVisible(searchParams.get('imported') === 'true');
  }, [searchParams]);

  /**
   * The queue load waits for the course catalog to answer.
   *
   * Until it has, the course in `filters` is whatever the URL said and has NOT been checked against
   * this session's grants — so firing the load first would fetch another course's work items and put
   * them on screen, briefly, with their approve buttons live. `GET /api/v1/queue/items` carries
   * `SessionAuthGuard` only; the course is a client-supplied filter there, not a guard, so nothing
   * downstream would refuse it. One extra wait on an already-parallel request buys that.
   */
  useEffect(() => {
    if (!filters.courseKey || !courseReconciled) {
      return;
    }
    void refreshQueue();
  }, [courseReconciled, filters]);

  useEffect(() => {
    if (!filters.courseKey) {
      setWaves([]);
      return;
    }

    let cancelled = false;
    void fetchReleaseWaves()
      .then((loadedWaves) => {
        if (!cancelled) {
          setWaves(loadedWaves.filter((wave) => wave.courseKey === filters.courseKey));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWaves([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters.courseKey]);

  // Section names come from the live course structure. A failure here is never fatal: the tree
  // keeps rendering with the derived/id labels rather than blocking the queue.
  useEffect(() => {
    const courseKey = filters.courseKey;
    if (!courseKey) {
      setSectionTitles(EMPTY_SECTION_TITLES);
      return;
    }

    // Drop the previous course's titles immediately: group ids are per-course, so keeping them
    // would label the new course's sections with the old course's names.
    setSectionTitles({ courseKey, titles: new Map(), settled: false });

    let cancelled = false;
    void fetchGroups(courseKey)
      .then((groups) => {
        if (!cancelled) {
          setSectionTitles({ courseKey, titles: buildGroupTitleIndex(groups), settled: true });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSectionTitles({ courseKey, titles: new Map(), settled: true });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters.courseKey]);

  /**
   * A SECOND, independent course chokepoint — one level at a time, enforced where it is rendered.
   *
   * The server already scopes this list (`queue.service.ts` `listByStatus` → `listByStatusForCourse`)
   * and that is the primary guarantee. This is the backstop, and it exists because the reviewer's
   * requirement is not "usually one level": *"there are topics that are similar but not tested
   * between levels and I wouldn't want to get my wires crossed upon review as the lines blur."* A
   * foreign item reaching this screen would render a plausible lesson under a header naming another
   * level, with live approve controls — and lesson numbers repeat across levels, so nothing else on
   * the page would contradict it.
   *
   * **The drop is COUNTED and SHOWN, never silent.** A guard that quietly discards would make "not
   * looking" the cheapest way to pass it, and a server regression would present as a correct screen.
   */
  const courseScoped = useMemo(() => {
    if (!filters.courseKey) return { items, foreignCount: 0 };
    const inCourse = items.filter((item) => item.courseKey === filters.courseKey);
    return { items: inCourse, foreignCount: items.length - inCourse.length };
  }, [filters.courseKey, items]);

  const filteredItems = useMemo(() => {
    let next = courseScoped.items;
    if (filters.taskType) {
      next = next.filter((item) => item.taskType === filters.taskType);
    }
    if (filters.search.trim()) {
      const needle = filters.search.trim().toLowerCase();
      next = next.filter((item) =>
        item.rationale.toLowerCase().includes(needle) ||
        item.clusterKey.toLowerCase().includes(needle) ||
        itemTitle(item).toLowerCase().includes(needle),
      );
    }
    return next;
  }, [courseScoped.items, filters.search, filters.taskType]);

  // The list loads at the selected status, or across ALL statuses when the filter is cleared
  // ("All Status"); the Section Tree is scoped to that same effective status to stay consistent
  // with what the queue actually shows.
  const effectiveStatus: WorkItemStatus | 'all' = filters.status || 'all';
  // Priority/wave are applied server-side and taskType/search client-side, but the stats breakdown
  // only models status. When any of those narrowing filters is active we build the tree from the
  // loaded (already-filtered) page so the sidebar exactly mirrors the visible list; otherwise we
  // use the course-wide breakdown so every section shows regardless of the selected section.
  const hasNarrowingFilter = Boolean(
    filters.priority || filters.waveId || filters.taskType || filters.search.trim(),
  );
  const sectionEntries = useMemo(
    () =>
      buildSectionEntries(
        hasNarrowingFilter ? undefined : stats?.countsBySection,
        filteredItems,
        effectiveStatus,
        filters.sectionId,
        sectionTitles.titles,
      ),
    [effectiveStatus, filteredItems, filters.sectionId, hasNarrowingFilter, sectionTitles, stats?.countsBySection],
  );
  // "All" === sum of the rendered section rows, by construction.
  const allSectionsCount = useMemo(
    () => sectionEntries.reduce((sum, entry) => sum + entry.count, 0),
    [sectionEntries],
  );
  const waveOptions = useMemo(
    () =>
      waves.map((wave) => ({
        value: wave.waveId,
        label: wave.waveId,
      })),
    [waves],
  );

  const sections = useMemo(() => {
    const groupItems = (sectionItems: WorkItemSummary[]) => {
      const groups = new Map<string, WorkItemSummary[]>();
      for (const item of sectionItems) {
        const list = groups.get(item.clusterKey) ?? [];
        list.push(item);
        groups.set(item.clusterKey, list);
      }
      return [...groups.entries()]
        .map(([clusterKey, clusterItems]) => ({ clusterKey, items: clusterItems }))
        // Order clusters by a STABLE key that does not change as members get approved/cancelled, so a
        // cluster never jumps position mid-review (AD feedback R-1). Ascending lowest cited page keeps
        // the reviewer moving forward through the curriculum (R-5); earliest createdAt then clusterKey
        // break ties deterministically.
        .sort((left, right) => compareClusters(left, right));
    };
    // Partition after grouping so review metadata and bulk actions keep the whole cluster.
    const groups = groupItems(filteredItems);
    return [
      { key: 'validation', groups: groups.filter(group => group.items.some(isValidationSample)) },
      { key: 'ordinary', groups: groups.filter(group => !group.items.some(isValidationSample)) },
    ] as const;
  }, [filteredItems]);

  const currentSectionLabel = useMemo(() => {
    if (!filters.sectionId) {
      return sectionEntries[0]?.label ?? courseLabel(filters.courseKey, availableCourses);
    }
    return sectionEntries.find((entry) => entry.sectionId === filters.sectionId)?.label
      ?? sectionTitles.titles.get(filters.sectionId)
      ?? `Section ${filters.sectionId}`;
  }, [availableCourses, filters.courseKey, filters.sectionId, sectionEntries, sectionTitles]);

  if (!filters.courseKey) {
    return <StatePanel message="Select a course to load the queue." tone="neutral" />;
  }

  if (initialLoading) {
    return <StatePanel message="Loading queue..." tone="neutral" />;
  }

  if (error) {
    return <StatePanel message={error} tone="error" />;
  }

  return (
    <>
      <aside className="flex w-64 flex-col gap-6 border-r border-slate-200 bg-white p-4">
        <div>
          <h3 className="mb-4 text-[11px] font-bold uppercase tracking-widest text-slate-400">Section Tree</h3>
          <div className="flex flex-col gap-1">
            <SectionButton
              active={!filters.sectionId}
              count={allSectionsCount}
              icon="list_alt"
              label="All"
              onClick={() => updateFilter('sectionId', '')}
              testId="section-all"
            />
            {/* Rows are ordered by label, so they are held back until the section names have
                settled — otherwise they visibly re-sort the moment the titles land, moving the
                click target the reviewer is already aiming at. */}
            {sectionTitles.settled
              ? sectionEntries.map((entry) => (
                <SectionButton
                  active={filters.sectionId === entry.sectionId}
                  count={entry.count}
                  icon={entry.icon}
                  key={entry.sectionId}
                  label={entry.label}
                  onClick={() => updateFilter('sectionId', entry.sectionId)}
                />
              ))
              : null}
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto bg-background-light">
        {refreshing ? <div className="h-0.5 w-full animate-pulse bg-primary" data-testid="queue-refreshing" /> : null}
        <div className="px-6 py-3">
          {bannerVisible ? (
            <div className="flex items-center justify-between rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-2.5">
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                <span className="material-symbols-outlined text-[18px]">check_circle</span>
                <span>{currentSectionLabel} run · {stats?.total ?? items.length} items imported · just now</span>
              </div>
              <button
                className={`text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 ${BUTTON_INTERACTION_STYLES}`}
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete('imported');
                  setSearchParams(next, { replace: true });
                  setBannerVisible(false);
                }}
                type="button"
              >
                Dismiss
              </button>
            </div>
          ) : null}
        </div>

        {courseScoped.foreignCount > 0 ? (
          <div className="px-6 pb-2">
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900"
              data-testid="foreign-course-items-notice"
            >
              The server returned {courseScoped.foreignCount} item
              {courseScoped.foreignCount === 1 ? '' : 's'} belonging to another course, which should
              be impossible: this queue asked only about
              {' '}{courseLabel(filters.courseKey, availableCourses)} ({filters.courseKey}).
              {courseScoped.foreignCount === 1 ? ' It is' : ' They are'} not shown, and nothing you
              have already decided is affected. Please report this before approving anything further
              — course scoping is what keeps one exam level's work out of another's.
            </div>
          </div>
        ) : null}

        {/* Always mounted: a live region created together with its text is not reliably announced,
            so an amber "not delivered to Slack" banner would be silent for a screen-reader user. */}
        <div className={actionBanner ? 'px-6 pb-2' : undefined} role="status">
          {actionBanner ? (
            <div className={`rounded-lg border px-4 py-2.5 text-sm ${BANNER_TONE_STYLES[actionBanner.tone]}`}>
              {actionBanner.message}
            </div>
          ) : null}
        </div>

        <div className="mb-2 px-6">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-1 items-center gap-3">
              <div className="relative max-w-sm flex-1">
                <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">search</span>
                <input
                  className="w-full rounded-md border border-slate-200 py-1.5 pl-9 pr-4 text-sm placeholder:text-slate-400 focus:border-primary focus:ring-1 focus:ring-primary"
                  onChange={(event) => updateFilter('search', event.target.value)}
                  placeholder="Search items..."
                  value={filters.search}
                />
              </div>
              <div className="mx-1 h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-2">
                <FilterDropdown
                  label="Task Type"
                  onChange={(value) => updateFilter('taskType', value)}
                  options={[
                    { value: 'structural', label: 'Structural' },
                    { value: 'content', label: 'Content' },
                    { value: 'practice', label: 'Practice' },
                    { value: 'practice_item', label: 'Practice item' },
                    { value: 'qa', label: 'QA' },
                  ]}
                  value={filters.taskType ?? ''}
                />
                <FilterDropdown
                  label="Status"
                  onChange={(value) => updateFilter('status', value)}
                  options={[
                    { value: 'pending', label: 'Pending' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'approved_with_edit', label: 'Approved with edit' },
                    { value: 'cancelled', label: 'Cancelled' },
                    { value: 'executed', label: 'Executed' },
                  ]}
                  value={filters.status ?? ''}
                />
                <FilterDropdown
                  label="Wave"
                  onChange={(value) => updateFilter('waveId', value)}
                  options={waveOptions}
                  value={filters.waveId ?? ''}
                />
              </div>
            </div>
            <button
              className={`px-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-primary ${BUTTON_INTERACTION_STYLES}`}
              onClick={clearFilters}
              type="button"
            >
              Clear All
            </button>
          </div>
        </div>

        <div className="flex items-end justify-between px-6 pb-4 pt-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Work Queue</h1>
            <p className="text-sm text-slate-500">{courseLabel(filters.courseKey, availableCourses)}</p>
          </div>
        </div>

        <QueueItemsContext.Provider value={courseScoped.items}>
        <div className="px-6 pb-12">
          {sections.map((section) => section.groups.length > 0 ? (
            <div data-testid={section.key === 'validation' ? 'validation-samples-section' : 'ordinary-queue-section'} key={section.key}>
              {section.key === 'validation' ? (
                <div className="mb-4">
                  <h2 className="text-lg font-bold text-slate-900">Validate these first</h2>
                  <p className="text-sm text-slate-500">Samples selected at import to validate their flow; review these before other work.</p>
                </div>
              ) : null}
          {section.groups.map((group) => {
            const meta = deriveClusterMeta(group.clusterKey, group.items);
            const lessonDrafts = meta.pending.filter(isLessonDraft);
            const bulkApprovable = meta.pending.filter((item) => !isLessonDraft(item) && !isStepAppend(item) && !isPlacedLesson(item) && !isNoSecondTry(item));
            return (
              <section className="mb-8" data-testid="cluster-card" key={`${section.key}:${group.clusterKey}`}>
                <div className="mb-4 mt-2 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {meta.isNewNodeCluster ? (
                        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700">
                          {clusterCreateBadgeLabel(meta)}
                        </span>
                      ) : (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                          Rewiring
                        </span>
                      )}
                      <h2 className="text-base font-bold text-slate-700">{meta.title}</h2>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span className="rounded border border-slate-100 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-400">
                        {group.clusterKey}
                      </span>
                      <span>{meta.breakdown}</span>
                      <span className={clusterStatusChipClass(meta.status)}>{clusterStatusLabel(meta.status)}</span>
                      {lessonDrafts.length > 0 ? (
                        <span className="font-medium text-amber-700">
                          {lessonDrafts.length} {lessonDrafts.length === 1 ? 'lesson' : 'lessons'} reviewed individually
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {meta.restorable.length > 0 ? (
                      <button
                        className={`flex items-center gap-1.5 rounded-lg border border-blue-200 px-3 py-1.5 text-sm font-bold text-blue-600 hover:bg-blue-50 ${BUTTON_INTERACTION_STYLES}`}
                        onClick={() => {
                          if (window.confirm(`Return ${meta.restorable.length} item(s) in "${meta.title}" to the queue?`)) {
                            void handleRestoreCluster(meta.restorable);
                          }
                        }}
                        type="button"
                      >
                        <span className="material-symbols-outlined text-[18px]">restore</span>
                        Restore cluster ({meta.restorable.length})
                      </button>
                    ) : null}
                    {meta.cancellable.length > 0 ? (
                      <button
                        className={`flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-bold text-slate-500 hover:bg-slate-50 ${BUTTON_INTERACTION_STYLES}`}
                        onClick={() => {
                          // prompt (not confirm) so a bulk rejection can carry a reason into the
                          // audit trail like single ones do; Esc/Cancel aborts.
                          //
                          // This is the ONE rejection path that does not use the per-kind
                          // vocabulary (§4.1), and deliberately so: a cluster can mix structural
                          // items, lesson drafts and practice items, so no single vocabulary
                          // applies, and the reason for a bulk rejection is usually a cohort
                          // decision ("regenerating wave 03") rather than an item defect. What was
                          // NOT defensible was accepting an empty answer as "no reason" — that let
                          // a whole cluster be rejected with nothing recorded at all
                          // (gpt-5.6-sol, 2026-07-30). Free prose, but mandatory prose.
                          const reason = window.prompt(
                            `Reject ${meta.cancellable.length} draft(s) in "${meta.title}"?\nReason, required (recorded in the audit trail):`,
                            '',
                          );
                          if (reason === null) {
                            return;
                          }
                          if (!reason.trim()) {
                            setActionBanner({
                              tone: 'error',
                              message: 'Rejecting a cluster needs a reason — nothing was rejected.',
                            });
                            return;
                          }
                          void handleCancelCluster(meta.cancellable, reason.trim());
                        }}
                        type="button"
                      >
                        <span className="material-symbols-outlined text-[18px]">cancel</span>
                        Reject cluster
                      </button>
                    ) : null}
                    {bulkApprovable.length > 0 ? (
                      <button
                        className={`flex items-center gap-2 rounded-lg bg-primary px-4 py-1.5 text-sm font-bold text-white hover:bg-slate-800 ${BUTTON_INTERACTION_STYLES}`}
                        onClick={() => {
                          void handleBulkApprove(bulkApprovable);
                        }}
                        type="button"
                      >
                        <span className="material-symbols-outlined text-[18px]">done_all</span>
                        Approve cluster ({bulkApprovable.length})
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  {meta.ordered.map((item) => (
                    <WorkItemRow
                      expanded={expandedId === item.workItemId}
                      isAnchor={meta.anchor?.workItemId === item.workItemId && meta.ordered.length > 1}
                      item={item}
                      key={item.workItemId}
                      transcriptHref={lessonTranscriptPath(item, searchParams)}
                      reviewHref={isPracticeItem(item)
                        ? practiceReviewPath(item.workItemId, searchParams)
                        : lessonReviewPath(item.workItemId, searchParams)}
                      onApprove={(notes) => {
                        void handleApprove(item.workItemId, notes);
                      }}
                      onApproveWithEdit={(reviewerPatch, notes) => {
                        void handleApproveWithEdit(item.workItemId, reviewerPatch, notes);
                      }}
                      onCancel={(notes) => {
                        void handleCancel(item.workItemId, notes);
                      }}
                      onComment={(text) => handleComment(item.workItemId, text)}
                      onRestore={() => {
                        void handleRestore(item.workItemId);
                      }}
                      onToggle={() => {
                        setActionBanner(null);
                        setExpandedId((current) => (current === item.workItemId ? null : item.workItemId));
                      }}
                    />
                  ))}
                </div>
              </section>
            );
          })}

            </div>
          ) : null)}

          {sections.every(section => section.groups.length === 0) ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
              No queue items match the current filters.
            </div>
          ) : null}
        </div>
        </QueueItemsContext.Provider>
      </main>
    </>
  );

  function syncUrl(next: DashboardFilters) {
    const params = new URLSearchParams(searchParams);
    const managedKeys = ['courseKey', 'sectionId', 'priority', 'status', 'waveId', 'search', 'taskType'];
    for (const key of managedKeys) {
      params.delete(key);
    }
    if (next.courseKey) params.set('courseKey', next.courseKey);
    if (next.sectionId) params.set('sectionId', next.sectionId);
    if (next.priority) params.set('priority', next.priority);
    // Always write status: "All Status" (undefined in state) becomes an explicit `status=all` so a
    // reload/share of the URL keeps the choice instead of falling back to the pending default.
    params.set('status', next.status ?? 'all');
    if (next.waveId) params.set('waveId', next.waveId);
    if (next.taskType) params.set('taskType', next.taskType);
    if (next.search) params.set('search', next.search);
    setSearchParams(params, { replace: true });
  }

  function updateFilter(key: keyof DashboardFilters, value: string) {
    setFilters((current) => {
      const next: DashboardFilters = {
        ...current,
        [key]: value || undefined,
      };
      if (key === 'search') {
        next.search = value;
      }
      return next;
    });
  }

  function clearFilters() {
    setFilters((current) => {
      return {
        status: 'pending',
        search: '',
        ...(current.courseKey ? { courseKey: current.courseKey } : {}),
      };
    });
  }

  async function refreshQueue() {
    // Always the live filters, never the ones this closure was created with.
    const active = filtersRef.current;
    if (!active.courseKey) {
      return;
    }

    // Supersede whatever is in flight: its answer is already known to be for the wrong filters.
    queueAbortRef.current?.abort();
    const controller = new AbortController();
    queueAbortRef.current = controller;
    const request = (queueRequestRef.current += 1);
    const mutationEpoch = mutationEpochRef.current;
    const isCurrent = () => queueRequestRef.current === request;

    const isInitial = items.length === 0;
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');

    try {
      const [queue, queueStats] = await Promise.all([
        fetchQueueItems(
          {
            courseKey: active.courseKey,
            ...(active.priority ? { priority: active.priority } : {}),
            ...(active.sectionId ? { sectionId: active.sectionId } : {}),
            // Undefined status = the "All Status" dropdown choice; ask the API for 'all' explicitly
            // (an omitted status would default back to pending server-side).
            status: active.status ?? 'all',
            ...(active.waveId ? { waveId: active.waveId } : {}),
          },
          controller.signal,
        ),
        fetchQueueStats(active.courseKey, controller.signal),
      ]);
      if (!isCurrent()) {
        return;
      }
      if (mutationEpochRef.current !== mutationEpoch) {
        // Answered from state the reviewer has since changed. Ask again rather than roll their
        // decision back on screen; the retry supersedes this request, so `finally` leaves the
        // loading indicators to it.
        void refreshQueue();
        return;
      }
      setItems(queue.items);
      setStats(queueStats);
      setExpandedId((current) => (queue.items.some((item) => item.workItemId === current) ? current : null));
    } catch (reason) {
      // A superseded request's failure (including its own abort) is not the reviewer's problem, and
      // must not blank out a list that loaded fine.
      if (!isCurrent()) {
        return;
      }
      setError(reason instanceof Error ? reason.message : 'Unable to load queue');
      setItems([]);
      setStats(null);
    } finally {
      // Only the live request owns the loading indicators; an abandoned one finishing early would
      // otherwise present the previous filters' rows as settled.
      if (isCurrent()) {
        setInitialLoading(false);
        setRefreshing(false);
      }
    }
  }

  async function handleApprove(workItemId: string, notes?: string) {
    try {
      const updated = notes
        ? await approveItem(workItemId, notes, false, filters.courseKey)
        : await approveItem(workItemId, undefined, false, filters.courseKey);
      applyMutationOutcome(updated.workItemId, updated.status);
      setActionBanner({ tone: 'success', message: `Approved ${workItemId}.` });
    } catch (reason) {
      await handleMutationError(reason, `Approval failed for ${workItemId}.`);
    }
  }

  async function handleCancel(workItemId: string, notes?: string) {
    try {
      const updated = await cancelItem(workItemId, notes, filters.courseKey);
      applyMutationOutcome(updated.workItemId, updated.status);
      setActionBanner({ tone: 'success', message: `Rejected ${workItemId}.` });
    } catch (reason) {
      await handleMutationError(reason, `Rejection failed for ${workItemId}.`);
    }
  }

  async function handleComment(workItemId: string, text: string) {
    try {
      const result = await commentItem(workItemId, text, filters.courseKey);
      setActionBanner(describeCommentDeliveryFor(workItemId, result.postedToSlack));
      return result;
    } catch (reason) {
      // Surface the failure (the composer swallows the rethrow and keeps the draft text).
      await handleMutationError(reason, `Comment failed for ${workItemId}.`);
      throw reason;
    }
  }

  async function handleRestore(workItemId: string) {
    try {
      const updated = await restoreItem(workItemId, undefined, filters.courseKey);
      applyMutationOutcome(updated.workItemId, updated.status);
      setActionBanner({ tone: 'success', message: `Returned ${workItemId} to the queue.` });
    } catch (reason) {
      await handleMutationError(reason, `Restore failed for ${workItemId}.`);
    }
  }

  async function handleApproveWithEdit(workItemId: string, suggestedPatch: Record<string, unknown>, notes?: string) {
    try {
      const updated = notes
        ? await approveItemWithEdit(workItemId, suggestedPatch, notes, false, filters.courseKey)
        : await approveItemWithEdit(workItemId, suggestedPatch, undefined, false, filters.courseKey);
      applyMutationOutcome(updated.workItemId, updated.status);
      setActionBanner({ tone: 'success', message: `Approved ${workItemId} with an edit.` });
    } catch (reason) {
      await handleMutationError(reason, `Approve with edit failed for ${workItemId}.`);
    }
  }

  /**
   * "Approve cluster" — and the one place the queue turns on-screen rows into ATOMIC GROUPS.
   *
   * An item may carry a frozen roster of siblings whose patches must all apply or none. The server
   * cross-checks any declared group against that roster, so submitting each row as its own
   * singleton (which is what this used to do) was refused `group_mismatch` and a grouped item could
   * not be bulk approved at all — 286 of ~926 production items (`L-0339`).
   *
   * Declaring the roster is necessary and is NOT sufficient: the reviewer only read the rows in
   * front of them, so the request also states which members they actually decided
   * (`approveWorkItemIds`), and any group with a sibling still awaiting review is WITHHELD here
   * with a sentence naming the item to go and read. Approving that sibling silently would write a
   * review event and an audit row asserting this reviewer read something they never opened.
   */
  async function handleBulkApprove(clusterItems: WorkItemSummary[]) {
    if (clusterItems.length === 0) {
      return;
    }
    try {
      // The queue list is filtered — by status, section, task type and search — so a sibling that
      // genuinely landed may simply not be loaded. Read the ones we do not hold before deciding;
      // a GET is a safe method and is exempt from the mutation throttle.
      // Built from the COURSE-SCOPED set, never from the raw response. Anything the course guard
      // withheld must not be able to resolve a roster member and enter the approval plan — an
      // approval is the one action here that writes to live content.
      const resolved = new Map(courseScoped.items.map((each) => [each.workItemId, each]));
      const unresolved = rosterMembersToResolve(clusterItems, resolved);
      if (unresolved.length > 0) {
        const reads = await Promise.allSettled(unresolved.map((id) => fetchWorkItem(id)));
        for (const read of reads) {
          // A FAILED read is not evidence of anything. The member stays unresolved and the planner
          // withholds its group, rather than the group being approved on an assumption.
          if (read.status !== 'fulfilled' || !read.value?.workItemId) continue;
          // AND a member from another course is not evidence either. `fetchWorkItem` is addressed by
          // id and can answer with any course; a roster naming a foreign id would otherwise
          // reintroduce, into an APPROVAL, exactly the item the course guard above withheld from the
          // screen. Approving is the one action here that writes to live content, and the server's
          // own check would accept it — it proves the reviewer MAY touch that course, which is true
          // of every level he is granted.
          if (filters.courseKey && read.value.courseKey !== filters.courseKey) continue;
          resolved.set(read.value.workItemId, read.value);
        }
      }

      const plan = planClusterApproval(clusterItems, resolved);
      const withheldMessages = plan.withheld.map(describeWithheldGroup);

      if (plan.groups.length === 0) {
        setActionBanner({
          tone: 'error',
          message: withheldMessages.join(' ') || 'Nothing in this cluster could be approved.',
        });
        return;
      }
      // Refuse rather than chunk. The endpoint rejects a human `apply` above this many declared
      // items (AD-38), and splitting the request to get under the line would either divide an
      // all-or-nothing roster or quietly evade the threshold.
      // The DECISIONS, not the declared ids. Closure can force a group's whole roster to be named
      // while most of it already landed, and refusing over that would refuse a handful of real
      // decisions because of siblings nobody is deciding again. The server counts the same quantity.
      if (plan.approveItemCount > BULK_APPROVE_HUMAN_ITEM_LIMIT) {
        setActionBanner({
          tone: 'error',
          message:
            `This cluster would approve ${plan.approveItemCount} items, and one reviewer cannot approve more than `
            + `${BULK_APPROVE_HUMAN_ITEM_LIMIT} items at once — the record would assert somebody read them all. `
            + 'Narrow the view (by section or search) and approve in smaller passes; the request is deliberately '
            + 'not split automatically, because splitting an all-or-nothing group is the failure this button exists to prevent.',
        });
        return;
      }

      const result = await bulkApproveGroups(plan.groups);
      // Items that were already approved converged rather than changed — they still have to leave
      // the pending list, or the screen keeps offering an item the server has already decided.
      //
      // Each item takes the status the SERVER reported for it, never a blanket `approved`. The
      // server's `already_approved` covers `approved_with_edit` too, so relabelling the whole set
      // rewrote a reviewer's edited approval as a plain one on screen and moved a count from
      // `approved_with_edit` to `approved` for a transition that never happened. Found by
      // cross-model review of this diff.
      const landedStatuses = new Map<string, WorkItemStatus>();
      for (const entry of result.report.groups.flatMap((group) => group.items)) {
        if (entry.outcome === 'approved' || entry.outcome === 'approved_trail_incomplete' || entry.outcome === 'already_approved') {
          landedStatuses.set(entry.workItemId, entry.status ?? 'approved');
        }
      }
      // A report that predates the per-item status (or a stubbed one) must still move the list.
      for (const workItemId of [...result.succeeded, ...result.alreadyApproved]) {
        if (!landedStatuses.has(workItemId)) {
          landedStatuses.set(workItemId, 'approved');
        }
      }
      if (landedStatuses.size > 0) {
        applyBulkListUpdate(landedStatuses);
        // Every item that landed, not only the ones in the cluster that was clicked: a group can
        // reach beyond it, and stats built from the cluster alone would silently under-count.
        applyBulkOutcome([...resolved.values()], landedStatuses);
      }
      // Green ONLY when every requested item is accounted for. A split group, a run the server
      // stopped early, or an approval whose audit trail may be missing all need the operator to
      // look — reporting those in a success banner is how a bulk operation lies.
      //
      // The two are reported TOGETHER, never one instead of the other: an earlier version returned
      // on `failed` first, so a run with one refusal and one trail-incomplete approval showed only
      // the ordinary refusal and the missing audit trail was never mentioned.
      const warnings = buildBulkApproveWarnings(result);
      if (result.failed.length > 0 || warnings.length > 0 || withheldMessages.length > 0) {
        const message = [
          ...(result.failed.length > 0 ? [buildBulkFailureMessage(result.failed)] : []),
          ...warnings,
          // A group this client refused to submit is as much a non-approval as one the server
          // refused, and the reviewer has to hear about both in the same breath.
          ...withheldMessages,
        ].join(' ');
        setActionBanner({ tone: 'error', message });
        await refreshQueue();
        return;
      }
      setActionBanner({ tone: 'success', message: buildBulkApproveSuccessMessage(result) });
      setExpandedId(null);
    } catch (reason) {
      await handleMutationError(reason, 'Bulk approval failed.');
    }
  }

  async function handleCancelCluster(clusterItems: WorkItemSummary[], notes?: string) {
    if (clusterItems.length === 0) {
      return;
    }
    try {
      const result = await bulkCancelItems(clusterItems.map((item) => item.workItemId), notes);
      if (result.succeeded.length > 0) {
        const landed = new Map(result.succeeded.map((id) => [id, 'cancelled' as WorkItemStatus]));
        applyBulkListUpdate(landed);
        applyBulkOutcome(clusterItems, landed);
      }
      if (result.failed.length > 0) {
        setActionBanner({
          tone: 'error',
          message: result.failed.map((failure) => `Failed to reject ${failure.workItemId}: ${failure.error}`).join(' '),
        });
        await refreshQueue();
        return;
      }
      setActionBanner({ tone: 'success', message: `Rejected ${result.succeeded.length} drafts.` });
      setExpandedId(null);
    } catch (reason) {
      await handleMutationError(reason, 'Cluster rejection failed.');
    }
  }

  async function handleRestoreCluster(clusterItems: WorkItemSummary[]) {
    if (clusterItems.length === 0) {
      return;
    }
    try {
      const result = await bulkRestoreItems(clusterItems.map((item) => item.workItemId));
      if (result.succeeded.length > 0) {
        // Restored items become pending and leave the current (e.g. Cancelled) list.
        const landed = new Map(result.succeeded.map((id) => [id, 'pending' as WorkItemStatus]));
        applyBulkListUpdate(landed);
        applyBulkOutcome(clusterItems, landed);
      }
      if (result.failed.length > 0) {
        setActionBanner({
          tone: 'error',
          message: result.failed.map((failure) => `Failed to restore ${failure.workItemId}: ${failure.error}`).join(' '),
        });
        await refreshQueue();
        return;
      }
      setActionBanner({ tone: 'success', message: `Returned ${result.succeeded.length} items to the queue.` });
      setExpandedId(null);
    } catch (reason) {
      await handleMutationError(reason, 'Cluster restore failed.');
    }
  }

  async function handleMutationError(reason: unknown, fallbackMessage: string) {
    const status = reason instanceof ApiRequestError ? reason.status : undefined;
    if (status === 409) {
      const details = reason instanceof ApiRequestError && reason.details && typeof reason.details === 'object'
        ? reason.details as Record<string, unknown> : null;
      const specificRefusal = details && ['reviewer_edit_shared_field', 'answer_gate_requires_regate', 'append_not_editable']
        .includes(String(details.reason));
      setActionBanner({ tone: 'error', message: specificRefusal
        ? describeApiError(reason, fallbackMessage)
        : 'This item changed on another reviewer session. Refreshing the queue.' });
      await refreshQueue();
      return;
    }

    setActionBanner({
      tone: 'error',
      message: reason instanceof Error ? reason.message || fallbackMessage : fallbackMessage,
    });
  }

  function applyMutationOutcome(workItemId: string, status: WorkItemSummary['status']) {
    mutationEpochRef.current += 1;
    const affected = items.find((entry) => entry.workItemId === workItemId);
    setExpandedId((current) => (current === workItemId ? null : current));
    // Keep the just-actioned item VISIBLE IN PLACE with its new status rather than dropping it from
    // the list (AD feedback R-1: approving one item used to make its whole cluster jump away and its
    // siblings "disappear"). The row now shows a resolved state inside its cluster, the cluster stays
    // put, and a manual refresh re-syncs the list to server truth for the active status filter.
    setItems((current) =>
      current.map((entry) => (entry.workItemId === workItemId ? { ...entry, status } : entry)),
    );
    // Keep BOTH the status totals and the per-section breakdown (which drives the Section Tree) in
    // sync so the sidebar counts decrement immediately on approve/cancel.
    if (affected) {
      setStats((current) =>
        current ? patchQueueStats(current, [{ sectionId: affected.sectionId, from: affected.status, to: status }]) : current,
      );
    }
  }

  function applyBulkOutcome(affected: WorkItemSummary[], landed: ReadonlyMap<string, WorkItemStatus>) {
    const transitions = affected.flatMap((item) => {
      const to = landed.get(item.workItemId);
      // A no-op is not a transition. An item that was already `approved_with_edit` and is reported
      // `already_approved` has not moved, and counting it would take one off that bucket and add
      // one to another for a decision nobody took.
      return to && to !== item.status ? [{ sectionId: item.sectionId, from: item.status, to }] : [];
    });
    if (transitions.length === 0) {
      return;
    }
    setStats((current) => (current ? patchQueueStats(current, transitions) : current));
  }

  // Reflect a completed bulk transition in the loaded list, keeping every actioned item visible in
  // place with its new status (same keep-in-place rule as applyMutationOutcome — R-1), so a bulk
  // approve/cancel/restore never makes the cluster vanish mid-review.
  function applyBulkListUpdate(landed: ReadonlyMap<string, WorkItemStatus>) {
    if (landed.size === 0) {
      return;
    }
    mutationEpochRef.current += 1;
    setItems((current) =>
      current.map((item) => {
        const to = landed.get(item.workItemId);
        return to ? { ...item, status: to } : item;
      }),
    );
  }
}

function WorkItemRow({
  item,
  expanded,
  isAnchor = false,
  onToggle,
  onApprove,
  onApproveWithEdit,
  onCancel,
  onComment,
  onRestore,
  reviewHref,
  transcriptHref,
}: {
  item: WorkItemSummary;
  expanded: boolean;
  isAnchor?: boolean;
  onToggle: () => void;
  onApprove: (notes?: string) => void;
  onApproveWithEdit: (reviewerPatch: Record<string, unknown>, notes?: string) => void;
  onCancel: (notes?: string) => void;
  onComment: (text: string) => Promise<unknown>;
  onRestore: () => void;
  reviewHref: string;
  transcriptHref: string | null;
}) {
  const opView = opPresentation(item);
  const attachment = practiceItemAttachment(item);
  const evidenceBadge = evidenceBadgeFor(item);
  const isCancelled = item.status === 'cancelled';
  const canApprove = item.status === 'pending';
  const canCancel = item.status === 'pending' || item.status === 'approved' || item.status === 'approved_with_edit';
  // Row-level ✕ opens the expanded cancel-reason form instead of cancelling straight away, so the
  // quick path can't silently skip the audit reason (R-7). Counter, not boolean: repeated clicks
  // re-arm the form. Reset on ordinary toggles so a later re-expand starts in view mode.
  const [cancelIntent, setCancelIntent] = useState(0);
  return (
    <div
      className={`cursor-pointer border-b border-slate-100 transition-colors group ${expanded ? 'bg-slate-50/50' : 'hover:bg-slate-50'}`}
      data-testid="work-item-row"
      data-work-item-id={item.workItemId}
    >
      <div
        className="flex items-center gap-4 px-4 py-3"
        onClick={() => {
          setCancelIntent(0);
          onToggle();
        }}
      >
        <span className="material-symbols-outlined text-slate-500 group-hover:text-primary">
          {expanded ? 'expand_less' : 'expand_more'}
        </span>
        <span
          aria-label={opView.tooltip}
          className="material-symbols-outlined text-slate-500"
          title={opView.tooltip}
        >
          {opView.icon}
        </span>
        <span
          className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-tighter text-slate-600"
          title={opView.tooltip}
        >
          {opView.label}
        </span>
        {isAnchor ? (
          <span
            className="material-symbols-outlined text-[16px] text-violet-500"
            title="Cluster anchor — the new node this cluster introduces"
          >
            hub
          </span>
        ) : null}
        {/* The full concept/module title is the reviewer's primary scan target — show it in full,
            no truncation, no hunting in the patch preview (AD feedback R-2). */}
        <span className="min-w-0 flex-1 text-sm font-semibold text-slate-900">
          <WorkItemTitle item={item} />
        </span>
        <span className={statusBadgeClass(item.status)}>
          {item.status.replaceAll('_', ' ')}
        </span>
        {isMachineActor(item.reviewerActorType ? { actorType: item.reviewerActorType } : undefined) ? (
          <MachineActorBadge authority={item.reviewerAuthority} testId="queue-machine-actor" />
        ) : null}
        {item.answerGate ? (
          <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-bold text-violet-700"
            data-testid="answer-gate-badge">
            machine-gated: {item.answerGate.class}
          </span>
        ) : null}
        {/* An atomic group is a fact about the CONTENT — a term renamed in a question stem by one
            item and in its matching answer option by another — so the reviewer has to be able to
            see it before deciding, not discover it in a refusal after clicking. */}
        {isInMultiMemberGroup(item) ? (
          <span
            className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700"
            data-testid="atomic-group-badge"
            title={
              'These changes must all ship together or none of them do — applying part of the set would leave the '
              + 'lesson contradicting itself. Every member has to be reviewed before any of them can be approved.'
            }
          >
            All-or-nothing · {atomicGroupSize(item)}
          </span>
        ) : null}
        {/* Decoupled practice items (ELPI lane): attachment badge from the PERSISTED work-item
            fields only (intendedParent / reconciler-mirrored attachedEntityId), never from the
            patch contents (red-team #9). */}
        {attachment === 'unattached' && !(item.op === 'create' && item.intendedParent?.newLessonWorkItemId) ? (
          <span
            className="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase text-amber-700"
            title="Awaiting its intended lesson — attaches automatically once the lesson is created"
          >
            unattached
          </span>
        ) : null}
        {/* Queue readiness badge (AD-30 decision 2): the reviewer sees the evidence gap before
            spending a click on the item. Informational only — this row's Approve is NOT gated, by
            design; the acknowledgement lives on the review surface where the banner explains it. */}
        {evidenceBadge ? (
          <span
            className="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase text-amber-700"
            title={evidenceBadge.tooltip}
          >
            {evidenceBadge.label}
          </span>
        ) : null}
        {attachment === 'attached' ? (
          <span
            className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase text-emerald-600"
            title={`Attached to lesson entity #${item.attachedEntityId}`}
          >
            attached → #{item.attachedEntityId}
          </span>
        ) : null}
        <div className="ml-2 flex shrink-0 items-center gap-2">
          {isLessonDraft(item) ? (
            <Link
              className={`rounded-md border border-blue-200 px-2 py-1 text-xs font-bold text-blue-700 hover:bg-blue-50 ${LINK_INTERACTION_STYLES}`}
              onClick={(event) => event.stopPropagation()}
              to={reviewHref}
            >
              Review lesson
            </Link>
          ) : null}
          {isCancelled ? (
            <button
              aria-label={`Restore ${item.workItemId}`}
              title="Return to queue"
              className={`material-symbols-outlined rounded-md p-1 text-blue-600 hover:bg-blue-50 ${BUTTON_INTERACTION_STYLES}`}
              onClick={(event) => {
                event.stopPropagation();
                onRestore();
              }}
              type="button"
            >
              restore
            </button>
          ) : null}
          {canApprove ? (
            <button
              aria-label={`Approve ${item.workItemId}`}
              className={`material-symbols-outlined rounded-md p-1 text-emerald-600 hover:bg-emerald-50 ${BUTTON_INTERACTION_STYLES}`}
              // The one-click approve cannot carry the missing-evidence acknowledgement (its state
              // lives in the expanded panel), and AD-30 decision 2 asks for the acknowledgement to be
              // RECORDED, not merely offered — so for an item the gate applies to it routes into the
              // panel instead of approving unacknowledged. Still not a hard gate: expanding is the
              // click the reviewer already makes, and reject/comment are untouched. Appends also
              // need their projected lesson loaded before the expanded panel permits approval.
              onClick={(event) => {
                event.stopPropagation();
                if (evidenceBadge || isStepAppend(item) || isPlacedLesson(item) || isNoSecondTry(item)) {
                  if (!expanded) onToggle();
                  return;
                }
                onApprove();
              }}
              title={isNoSecondTry(item) ? 'Open the complete one-attempt review before approving' : isPlacedLesson(item) ? 'Approve a new lesson on its lesson review page, where its place in the course is checked'
                : isStepAppend(item) ? 'Read the lesson preview in the expanded item to approve' : evidenceBadge
                ? 'Missing evidence — acknowledge it in the expanded item to approve'
                : undefined}
              type="button"
            >
              check
            </button>
          ) : null}
          {canCancel ? (
            <button
              aria-label={`Reject ${item.workItemId}`}
              className={`material-symbols-outlined rounded-md p-1 text-slate-500 hover:bg-slate-100 ${BUTTON_INTERACTION_STYLES}`}
              onClick={(event) => {
                event.stopPropagation();
                if (!expanded) {
                  onToggle();
                }
                setCancelIntent((current) => current + 1);
              }}
              type="button"
            >
              close
            </button>
          ) : null}
        </div>
      </div>
      {expanded ? (
        <WorkItemExpanded
          cancelIntent={cancelIntent}
          item={item}
          onApprove={onApprove}
          onApproveWithEdit={onApproveWithEdit}
          onCancel={onCancel}
          onComment={onComment}
          onRestore={onRestore}
          reviewHref={reviewHref}
          transcriptHref={transcriptHref}
        />
      ) : null}
    </div>
  );
}

function WorkItemExpanded({
  cancelIntent = 0,
  item,
  onApprove,
  onApproveWithEdit,
  onCancel,
  onComment,
  onRestore,
  reviewHref,
  transcriptHref,
}: {
  cancelIntent?: number;
  item: WorkItemSummary;
  onApprove: (notes?: string) => void;
  onApproveWithEdit: (reviewerPatch: Record<string, unknown>, notes?: string) => void;
  onCancel: (notes?: string) => void;
  onComment: (text: string) => Promise<unknown>;
  onRestore: () => void;
  reviewHref: string;
  transcriptHref: string | null;
}) {
  const stepAppend = isStepAppend(item);
  const placedLesson = isPlacedLesson(item) || isNoSecondTry(item);
  const [appendLoadedId, setAppendLoadedId] = useState<string | null>(null);
  const onAppendLoaded = useCallback((loaded: boolean) => {
    setAppendLoadedId(loaded ? item.workItemId : null);
  }, [item.workItemId]);
  const isCancelled = item.status === 'cancelled';
  const canApprove = item.status === 'pending' || item.status === 'cancelled';
  const canCancel = item.status === 'pending' || item.status === 'approved' || item.status === 'approved_with_edit';
  const envelope = isPureEditsEnvelope(item.suggestedPatch);
  const hasTitle = Object.hasOwn(item.suggestedPatch, 'title');
  const prereqOnly = isPrerequisiteOnlyPatch(item.suggestedPatch);
  const structuralOp = ['archive', 'move', 'reorder'].includes(item.op);
  const editUnavailableReason = isNoSecondTry(item)
    ? 'This conversion requires a fresh cleared item for any edit. Open its review screen to inspect every branch before approval.'
    : item.answerGate
    ? 'Approve with edit is not available for this question: its answer was checked by two independent solvers against exactly this text, so any change needs a newly checked version. Reject it with your wording as a comment and it will be redrafted.'
    : structuralOp
      ? 'This change moves or retires content and has no text to edit here. Approve it, or reject it with a comment.'
      : !(envelope || prereqOnly || hasTitle)
        ? 'This change has no text that can be edited here. Approve it, or reject it with a comment.'
        : null;

  const [mode, setMode] = useState<'view' | 'edit' | 'cancel'>('view');
  const [titleDraft, setTitleDraft] = useState(() => suggestedTitleValue(item.suggestedPatch));

  // Row ✕ routes here: each bump of the intent counter opens the cancel-reason form.
  useEffect(() => {
    if (cancelIntent > 0) {
      setMode('cancel');
    }
  }, [cancelIntent]);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState<ReviewNoteEvent[]>([]);
  const [posting, setPosting] = useState(false);
  const [evidenceAcknowledged, setEvidenceAcknowledged] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchWorkItemHistory(item.workItemId)
      .then((events) => {
        if (!cancelled) {
          setComments(events.filter(isReviewNoteEvent));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setComments([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [item.workItemId]);

  const evidence = describeSources(item.sourceRefs);
  // Missing-evidence soft gate, same rule as the review surface (AD-30 #2, AD-33 #3).
  const evidenceReadinessState = evidenceReadiness(item);
  const evidenceGateShown = evidenceGateApplies(item) && evidenceReadinessState !== 'ready';
  const evidenceGateOpen = !evidenceGateShown || evidenceAcknowledged;
  // Same composer as the review surface. The queue row has no follow-up tag row of its own (§4.3
  // puts the tags where the item is actually read), but it must not hand-roll the note format either:
  // one function owns what an approve records, or the day either record changes only one of the two
  // surfaces changes with it.
  const approveNotes = buildApproveNotes({ tags: [], acknowledgeEvidence: evidenceGateShown });
  // One queue lists structural items, lesson drafts and practice items side by side, so the reject
  // form takes its vocabulary from the row it is attached to (§4.1).
  const rejectionReasons = getRejectionReasons(item.courseKey, rejectionItemKind(item));
  const rejectionNoteMandatory = rejectionNoteRequired(rejectionReasons, cancelReason);
  const lessonDraft = isLessonDraft(item) || isNoSecondTry(item) && item.targetType === 'entity';
  const practiceItem = isPracticeItem(item);
  const reviewerBrief = lessonDraft ? deriveReviewerBrief(item) : null;
  const ratingEntries = lessonDraft && item.latestRatings
    ? resolveStoredRatings(item.courseKey, 'lesson', item.latestRatings.ratings)
    : [];

  async function submitComment() {
    const text = comment.trim();
    if (!text || posting) {
      return;
    }
    setPosting(true);
    try {
      await onComment(text);
      setComment('');
      const events = await fetchWorkItemHistory(item.workItemId);
      setComments(events.filter(isReviewNoteEvent));
    } catch {
      // The error banner is surfaced by the parent handler.
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="grid grid-cols-12 gap-6 border-b border-slate-100 bg-white p-6">
      <div className={`${practiceItem ? 'col-span-8' : 'col-span-4'} flex flex-col gap-4`}>
        {evidenceGateShown ? (
          <EvidenceGate
            acknowledged={evidenceAcknowledged}
            onAcknowledge={setEvidenceAcknowledged}
            readiness={evidenceReadinessState}
            variant="row"
          />
        ) : null}
        {practiceItem ? (
          <PracticeItemMetadata item={item} />
        ) : <div>
          <h4 className="mb-2 text-xs font-bold uppercase text-slate-400">
            {reviewerBrief ? 'Reviewer brief' : 'Rationale'}
          </h4>
          {reviewerBrief ? (
            <>
              <p className="text-sm leading-relaxed text-slate-700">{reviewerBrief.learnerOutcome}</p>
              <p className="mt-2 text-xs font-semibold text-slate-500">
                {reviewerBrief.lessonShape}
                {reviewerBrief.estimatedMinutes != null ? ` · Estimated ${reviewerBrief.estimatedMinutes} min` : ''}
              </p>
              {ratingEntries.length > 0 ? (
                <div aria-label={`Ratings for ${item.workItemId}`} className="mt-2 flex flex-wrap items-center gap-1.5">
                  {ratingEntries.map((entry) => (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-800" key={entry.key}>
                      {compactRatingLabel(entry.key, entry.label)} {entry.value}/5
                    </span>
                  ))}
                </div>
              ) : null}
              <details className="mt-3 text-xs text-slate-600">
                <summary className="cursor-pointer font-bold text-slate-700">Technical details</summary>
                <div className="mt-2 max-h-80 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <h5 className="font-bold uppercase text-slate-500">Rationale</h5>
                  <p className="mt-1 whitespace-pre-wrap leading-relaxed">{item.rationale}</p>
                  <h5 className="mt-3 font-bold uppercase text-slate-500">Suggested patch</h5>
                  <pre className="mt-1 whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-slate-700">
                    {JSON.stringify(item.suggestedPatch, null, 2)}
                  </pre>
                </div>
              </details>
            </>
          ) : (
            <p className="text-sm leading-relaxed text-slate-700">{item.rationale}</p>
          )}
        </div>}
        <div>
          <h4 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase text-slate-400">
            <span className="material-symbols-outlined text-[14px]">forum</span>
            Comments
          </h4>
          {comments.length > 0 ? (
            <div className="mb-2 flex flex-col gap-2">
              {comments.map((event, index) => (
                <div
                  className="rounded-lg border border-slate-100 bg-slate-50 p-2"
                  data-testid="review-history-entry"
                  key={`${event.timestamp}-${index}`}
                >
                  <div className="flex items-center justify-between text-[10px] text-slate-400">
                    <span className="flex items-center gap-1.5">
                      <span className="font-semibold text-slate-500">{event.reviewerEmail}</span>
                      {/* A decision's note is not something someone typed into the comment box; say which it was. */}
                      {event.action === 'comment' ? null : (
                        <span className="rounded bg-slate-200 px-1 py-px font-semibold uppercase tracking-wide text-slate-600">
                          {NOTED_ACTION_LABELS[event.action]}
                        </span>
                      )}
                      {/* …and whether a person made it. The address above is the operator who ran
                          the batch, not someone who opened this item. */}
                      {isMachineActor(event) ? (
                        <MachineActorBadge authority={event.actorAuthority} testId="review-history-machine-actor" />
                      ) : null}
                    </span>
                    <span>{formatTimestamp(event.timestamp)}</span>
                  </div>
                  {isMachineActor(event) ? <MachineAuthorityLine authority={event.actorAuthority} /> : null}
                  {event.notes ? (
                    <p className="mt-1 whitespace-pre-wrap text-xs text-slate-700">{event.notes}</p>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="mb-2 text-xs text-slate-400">No comments yet.</p>
          )}
          <textarea
            aria-label={`Comment on ${item.workItemId}`}
            className="w-full rounded-lg border border-slate-200 p-2 text-xs focus:border-primary focus:ring-1 focus:ring-primary"
            maxLength={4000}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Add a comment — posts to the Slack channel…"
            rows={2}
            value={comment}
          />
          <button
            className={`mt-1.5 flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-900 ${BUTTON_INTERACTION_STYLES}`}
            disabled={!comment.trim() || posting}
            onClick={() => void submitComment()}
            type="button"
          >
            <span className="material-symbols-outlined text-[16px]">send</span>
            {posting ? 'Posting…' : 'Post to Slack'}
          </button>
        </div>
      </div>
      {!practiceItem ? <div className="col-span-4 border-x border-slate-100 px-6">
        <h4 className="mb-2 text-xs font-bold uppercase text-slate-400">Evidence</h4>
        {evidence.length > 0 ? (
          <div className="flex flex-col gap-3">
            {evidence.map((ref, index) => (
              <div
                className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                key={`evidence-${ref.path}-${index}`}
              >
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined mt-0.5 text-rose-500">picture_as_pdf</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800">{ref.fileName}</div>
                    {/* One page number — the PRINTED book page the reviewer cross-references (R-4). */}
                    {ref.printedPage != null ? (
                      <div className="text-xs text-slate-500">Printed page {ref.printedPage}</div>
                    ) : ref.filePage != null ? (
                      <div className="text-xs text-slate-500">Page {ref.filePage}</div>
                    ) : null}
                    {ref.notes.length > 0 ? (
                      <div className="mt-2 flex flex-col gap-1.5">
                        {ref.notes.slice(0, 3).map((note) => (
                          <div
                            className="border-l-2 border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-slate-700 italic"
                            key={note}
                          >
                            {note}
                          </div>
                        ))}
                        {ref.notes.length > 3 ? (
                          <div className="text-xs text-slate-400">+{ref.notes.length - 3} more excerpts</div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  {ref.availability === 'missing' ? (
                    <span className="rounded bg-amber-50 px-2 py-1 text-[11px] font-bold text-amber-700">
                      Source not ingested
                    </span>
                  ) : (
                    <a
                      className={`flex items-center gap-1 text-[11px] font-bold text-primary hover:underline ${LINK_INTERACTION_STYLES}`}
                      href={sourceDocumentHref(ref.path, ref.filePage)}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Open full source{ref.printedPage != null ? ` at p.${ref.printedPage}` : ''}
                      <span className="material-symbols-outlined text-[14px]">open_in_new</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-48 flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50">
            <span className="material-symbols-outlined text-3xl text-slate-300">source</span>
            <p className="mt-2 text-sm text-slate-400">No evidence attached</p>
          </div>
        )}
      </div> : null}
      <div className="col-span-4 flex flex-col gap-4">
        {stepAppend ? <AppendPreview key={item.workItemId} item={item} courseId={item.courseKey} onLoaded={onAppendLoaded} /> : !lessonDraft && !practiceItem ? (
          <>
            <h4 className="mb-2 text-xs font-bold uppercase text-slate-400">Patch Preview</h4>
            {/*
              `suggestedPatch`, deliberately — NOT `reviewerPatch ?? suggestedPatch`. A row here can
              be `cancelled`, a cancelled row is directly approvable (`canApprove` above), and a plain
              approve CLEARS a stale `reviewerPatch` left by an earlier approve-with-edit so that the
              suggestion executes (`queue.service.ts` around the `['reviewerPatch']` clear list,
              guarded by `workflow-integration.test.ts`). Showing the effective patch here would
              therefore preview text that pressing Approve discards. The answer-wiring block below
              reads the same patch for the same reason. Found by cross-model review of this diff.
            */}
            <PatchPreview
              courseId={item.courseKey}
              entityId={item.targetType === 'entity' && item.targetId ? Number(item.targetId) : null}
              patch={item.suggestedPatch}
            />
            {/*
              The diff above shows one field's before/after and nothing around it, so a defect living
              in the RELATIONSHIP between that field and the rest of the lesson — a stem that now
              states its own answer key, an answer whose feedback step still describes the old stem —
              is invisible on this row. This renders the answer wiring of the step the edit's locator
              names, and only that. It returns null for everything else, so no other item type is
              affected. See StepWiringPane.tsx for why it shows LIVE text rather than a projection.
            */}
            <StepWiringPane item={item} />
          </>
        ) : null}
        <div className="mt-auto flex flex-col gap-3">
          {lessonDraft ? (
            <Link
              className={`flex items-center justify-center gap-1 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-bold hover:bg-blue-700 ${PRIMARY_ACTION_LINK_STYLES} ${LINK_INTERACTION_STYLES}`}
              to={reviewHref}
            >
              Open lesson review
              <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </Link>
          ) : null}
          {practiceItem ? (
            <Link
              className={`flex items-center justify-center gap-1 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-bold hover:bg-blue-700 ${PRIMARY_ACTION_LINK_STYLES} ${LINK_INTERACTION_STYLES}`}
              to={reviewHref}
            >
              Preview practice item
              <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </Link>
          ) : null}
          {item.taskType === 'structural' && !isNoSecondTry(item) ? (
            <Link
              className={`flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:underline ${LINK_INTERACTION_STYLES}`}
              to={`/queue/structural?courseKey=${encodeURIComponent(item.courseKey)}&sectionId=${encodeURIComponent(item.sectionId)}&clusterKey=${encodeURIComponent(item.clusterKey)}`}
            >
              Review in entity editor <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
            </Link>
          ) : null}
          {transcriptHref ? (
            <Link
              className={`flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:underline ${LINK_INTERACTION_STYLES}`}
              to={transcriptHref}
            >
              {/*
                A task target is a standalone practice item, not a lesson, and 189 of 344 fix-backlog
                items target one — calling it "the whole lesson" would be wrong on the majority of
                rows. "whole" and "read-only" are both load-bearing either way: the row already shows
                PART of the content, and the reader must not expect to edit here.
              */}
              {item.targetType === 'task'
                ? 'Open the whole practice item (read-only)'
                : 'Open the whole lesson (read-only)'}
              <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
            </Link>
          ) : null}
          {/*
            A prerequisite-only item edits its CONCEPT-MAP EDGES, not its title. The title input
            below cannot express that change at all, and it is worse than merely absent: injecting a
            `title` key makes `isEdgeOnlyPatch` false (`release.service.ts`), which routes the write
            through the whole-document content validator — the one that refuses most published
            lessons. The button being disabled without a title was the only thing preventing it.
          */}
          {!stepAppend && !placedLesson && mode === 'edit' && prereqOnly ? (
            <PrerequisiteEditor
              courseId={item.courseKey}
              disabled={!evidenceGateOpen}
              entityId={item.targetType === 'entity' && item.targetId ? Number(item.targetId) : null}
              onDiscard={() => setMode('view')}
              onSubmit={(reviewerPatch) => onApproveWithEdit(reviewerPatch, approveNotes)}
              patch={item.suggestedPatch}
            />
          ) : !stepAppend && !placedLesson && mode === 'edit' && envelope ? (
            <EditsEnvelopeEditor
              disabled={!evidenceGateOpen}
              item={item}
              onDiscard={() => setMode('view')}
              onSubmit={(patch) => onApproveWithEdit(patch, approveNotes)}
            />
          ) : !stepAppend && !placedLesson && mode === 'edit' && hasTitle ? (
            <div className="flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50/50 p-3">
              <label className="text-[11px] font-bold uppercase text-slate-500" htmlFor={`edit-title-${item.workItemId}`}>
                Edit title, then approve
              </label>
              <input
                autoFocus
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                id={`edit-title-${item.workItemId}`}
                onChange={(event) => setTitleDraft(event.target.value)}
                value={titleDraft}
              />
              <div className="flex items-center gap-2">
                <button
                  className={`flex-1 rounded bg-primary py-2 text-xs font-bold text-white hover:bg-slate-800 ${BUTTON_INTERACTION_STYLES}`}
                  // The gate stays visible in the left column while this form is open, so the
                  // acknowledgement can be REVOKED here. Without this the revoked tick still shipped
                  // `EVIDENCE_ACK_NOTE` — an acknowledgement written to the audit trail that the
                  // reviewer had withdrawn (gpt-5.6-sol, 2026-07-30).
                  disabled={!titleDraft.trim() || !evidenceGateOpen}
                  onClick={() => onApproveWithEdit({ ...item.suggestedPatch, title: titleDraft.trim() }, approveNotes)}
                  type="button"
                >
                  Save &amp; approve
                </button>
                <button
                  className={`rounded border border-slate-200 px-3 py-2 text-xs font-bold text-slate-500 hover:bg-slate-50 ${BUTTON_INTERACTION_STYLES}`}
                  onClick={() => setMode('view')}
                  type="button"
                >
                  Discard
                </button>
              </div>
            </div>
          ) : mode === 'cancel' ? (
            <div className="flex flex-col gap-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
              <span className="text-[11px] font-bold uppercase text-slate-500">Why reject this draft?</span>
              <div className="flex flex-wrap gap-1.5">
                {rejectionReasons.map((reason) => (
                  <button
                    aria-pressed={cancelReason === reason.label}
                    className={`${
                      cancelReason === reason.label
                        ? 'rounded-full bg-rose-600 px-2.5 py-1 text-[11px] font-bold text-white'
                        : 'rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50'
                    } ${BUTTON_INTERACTION_STYLES}`}
                    key={reason.label}
                    onClick={() => setCancelReason((current) => (current === reason.label ? '' : reason.label))}
                    title={reason.hint}
                    type="button"
                  >
                    {reason.label}
                  </button>
                ))}
              </div>
              {/* See PracticeItemReview: a `title`-only hint reaches neither keyboard nor
                  screen-reader users. `aria-live`, not `role="status"` — the dashboard already has
                  one status region for Slack delivery. */}
              <p aria-live="polite" className="text-[11px] leading-relaxed text-slate-600" data-testid="rejection-reason-hint">
                {findRejectionReason(rejectionReasons, cancelReason)?.hint ?? ''}
              </p>
              <textarea
                aria-label="Rejection note"
                aria-required={rejectionNoteMandatory}
                className="w-full rounded border border-slate-300 p-2 text-xs focus:border-primary focus:ring-1 focus:ring-primary"
                maxLength={4000}
                onChange={(event) => setCancelNote(event.target.value)}
                placeholder={rejectionNoteMandatory
                  ? 'Required — say what is wrong (recorded in the audit trail)…'
                  : 'Optional note (recorded in the audit trail)…'}
                rows={2}
                value={cancelNote}
              />
              <div className="flex items-center gap-2">
                <button
                  className={`flex-1 rounded bg-rose-600 py-2 text-xs font-bold text-white hover:bg-rose-700 ${BUTTON_INTERACTION_STYLES}`}
                  disabled={!canConfirmRejection(rejectionReasons, cancelReason, cancelNote)}
                  onClick={() => onCancel(buildCancelNotes(cancelReason, cancelNote))}
                  type="button"
                >
                  Confirm rejection
                </button>
                <button
                  className={`rounded border border-slate-200 px-3 py-2 text-xs font-bold text-slate-500 hover:bg-slate-50 ${BUTTON_INTERACTION_STYLES}`}
                  onClick={() => setMode('view')}
                  type="button"
                >
                  Back
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {isCancelled ? (
                <button
                  className={`flex-1 rounded border border-blue-300 bg-blue-50 py-2 text-xs font-bold text-blue-700 hover:bg-blue-100 ${BUTTON_INTERACTION_STYLES}`}
                  onClick={onRestore}
                  type="button"
                >
                  Return to queue
                </button>
              ) : null}
              {canApprove && placedLesson ? (
                <p className="flex-1 text-xs text-slate-600" data-testid="placed-lesson-approve-note">
                  {isNoSecondTry(item) ? 'Open the complete one-attempt review before approving. Any edit requires a fresh cleared item.'
                    : 'Approve this new lesson on its lesson review page, where its place in the course and its prerequisites are checked against the live course.'}
                </p>
              ) : null}
              {canApprove && !placedLesson ? (
                <button
                  className={`flex-1 rounded bg-primary py-2 text-xs font-bold text-white shadow-sm hover:bg-slate-800 ${BUTTON_INTERACTION_STYLES}`}
                  disabled={!evidenceGateOpen || (stepAppend && appendLoadedId !== item.workItemId)}
                  onClick={() => onApprove(approveNotes)}
                  title={stepAppend && appendLoadedId !== item.workItemId ? 'The lesson preview must load and be ready for release before approving' : evidenceGateOpen ? undefined : 'Tick the missing-evidence acknowledgement to approve'}
                  type="button"
                >
                  Approve
                </button>
              ) : null}
              {canApprove && !stepAppend && !placedLesson && editUnavailableReason === null ? (
                <button
                  className={`flex-1 rounded border border-slate-200 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 ${BUTTON_INTERACTION_STYLES}`}
                  disabled={!evidenceGateOpen}
                  onClick={() => {
                    setTitleDraft(suggestedTitleValue(item.suggestedPatch));
                    setMode('edit');
                  }}
                  title={evidenceGateOpen ? undefined : 'Tick the missing-evidence acknowledgement to approve'}
                  type="button"
                >
                  Approve with edit
                </button>
              ) : null}
              {canApprove && !stepAppend && !placedLesson && editUnavailableReason ? (
                <p className="flex-1 text-xs text-slate-500" data-testid="approve-with-edit-unavailable">{editUnavailableReason}</p>
              ) : null}
              {canCancel ? (
                <button
                  className={`rounded border border-slate-200 px-3 py-2 text-xs font-bold text-slate-500 hover:bg-slate-50 ${BUTTON_INTERACTION_STYLES}`}
                  onClick={() => setMode('cancel')}
                  type="button"
                >
                  Reject draft…
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// The editable title value from a suggested patch (string title, or the `after`/`before` of a diff
// tuple). Empty when the patch carries no title.
function suggestedTitleValue(patch: Record<string, unknown>): string {
  const title = patch.title;
  if (typeof title === 'string') {
    return title;
  }
  if (title && typeof title === 'object') {
    const tuple = title as { before?: unknown; after?: unknown };
    if (typeof tuple.after === 'string') {
      return tuple.after;
    }
    if (typeof tuple.before === 'string') {
      return tuple.before;
    }
  }
  return '';
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function SectionButton({
  icon,
  label,
  count,
  active,
  onClick,
  testId,
}: {
  icon: string;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  /** Only the "All" row carries one: its count is the whole-queue total a test needs to address. */
  testId?: string;
}) {
  return (
    <button
      aria-pressed={active}
      data-testid={testId}
      className={`${active
        ? 'flex items-center justify-between rounded-lg bg-primary/10 px-3 py-2 text-primary'
        : 'flex items-center justify-between rounded-lg px-3 py-2 text-slate-600 hover:bg-slate-50'} ${BUTTON_INTERACTION_STYLES}`}
      onClick={onClick}
      type="button"
    >
      <div className="flex items-center gap-3">
        <span className={`material-symbols-outlined ${active ? '' : 'opacity-70'}`}>
          {active ? 'folder_open' : icon}
        </span>
        <span className={`text-sm ${active ? 'font-bold' : 'font-medium'}`}>{label}</span>
      </div>
      <span className={`text-xs ${active ? 'font-bold' : 'font-semibold text-slate-500'}`}>{count}</span>
    </button>
  );
}

function FilterDropdown({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const optionList = useMemo(() => [{ value: '', label: `All ${label}` }, ...options], [label, options]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handler = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const currentIndex = optionList.findIndex((option) => option.value === value);
    setActiveIndex(currentIndex >= 0 ? currentIndex : 0);
  }, [open, optionList, value]);

  return (
    <div className="relative group" ref={ref}>
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium ${
          value
            ? 'border border-primary bg-primary/5 text-primary'
            : 'border border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50'
        } ${BUTTON_INTERACTION_STYLES}`}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
        type="button"
      >
        {value || label}
        <span className="material-symbols-outlined text-[18px] text-slate-500">expand_more</span>
      </button>
      {open ? (
        <div
          className="absolute left-0 top-full z-20 mt-1 min-w-[140px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              setOpen(false);
              return;
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActiveIndex((current) => Math.min(optionList.length - 1, current + 1));
              return;
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex((current) => Math.max(0, current - 1));
              return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              const option = optionList[activeIndex];
              onChange(option?.value ?? '');
              setOpen(false);
            }
          }}
          role="listbox"
          tabIndex={-1}
        >
          {optionList.map((option, index) => (
            <button
              aria-selected={value === option.value}
              className={`w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${
                value === option.value ? 'font-bold text-primary' : 'text-slate-700'
              } ${activeIndex === index ? 'bg-slate-50' : ''} ${BUTTON_INTERACTION_STYLES}`}
              data-active={activeIndex === index ? 'true' : 'false'}
              key={option.value || `all-${label}`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              role="option"
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function StatePanel({ message, tone }: { message: string; tone: 'error' | 'neutral' }) {
  return (
    <div className={tone === 'error'
      ? 'grid min-h-[18rem] place-items-center rounded-[1.5rem] border border-rose-200 bg-rose-50 px-6 text-center text-sm text-rose-800 shadow-sm'
      : 'grid min-h-[18rem] place-items-center rounded-[1.5rem] border border-slate-200 bg-white px-6 text-center text-sm text-slate-500 shadow-sm'}>
      <span className={tone === 'neutral' ? 'animate-pulse' : ''}>{message}</span>
    </div>
  );
}

/**
 * The filters a queue URL asks for.
 *
 * **The course is carried through unresolved when the catalog has not arrived yet.** This runs in a
 * `useState` initialiser, on the first render, before `fetchCourses` has answered — so `courses` is
 * `[]` and `selectAuthoringCourseKey` cannot confirm anything against it and answers `''`. Dropping
 * the course there is what made `/queue?courseKey=350` end up on `/queue?courseKey=12b&status=pending`
 * with `12b`'s items on screen and its approve buttons live: the empty slot was then filled with the
 * *preferred* course and `syncUrl` rewrote the address bar. Lesson ids collide across courses, so
 * that is a reviewer deciding one course's content believing it is another's — not a cosmetic
 * mismatch. The grant check is not skipped, only deferred: {@link WorkQueueDashboard} reconciles the
 * carried value against the catalog the moment it loads, and replaces it if it is not authorised.
 */
function parseFiltersFromUrl(searchParams: URLSearchParams, courses: CourseSummary[]): DashboardFilters {
  const requestedCourseKey = readCourseParam(searchParams);
  const courseKey =
    courses.length > 0
      ? resolveCourseSelection(courses, requestedCourseKey).courseKey
      : requestedCourseKey ?? '';
  const priority = searchParams.get('priority');
  const sectionId = searchParams.get('sectionId');
  const taskType = searchParams.get('taskType');
  const waveId = searchParams.get('waveId');
  // `status=all` is the URL form of the "All Status" choice (state: undefined status); a URL
  // without status opens the queue on the actionable default, pending.
  const statusParam = searchParams.get('status');
  const status: WorkItemStatus | undefined =
    statusParam === 'all' ? undefined : ((statusParam as WorkItemStatus | null) ?? 'pending');

  return {
    search: searchParams.get('search') ?? '',
    ...(status ? { status } : {}),
    ...(courseKey ? { courseKey } : {}),
    ...(priority ? { priority: priority as Priority } : {}),
    ...(sectionId ? { sectionId } : {}),
    ...(taskType ? { taskType: taskType as TaskType } : {}),
    ...(waveId ? { waveId } : {}),
  };
}

function courseLabel(courseKey: string | undefined, courses: CourseSummary[]) {
  if (!courseKey) {
    return 'Course queue';
  }
  return courses.find((course) => course.id === courseKey)?.title ?? courseKey;
}

// Builds the Section Tree rows. Prefers the course-wide stats breakdown (every section, scoped to
// the active status) so the list is complete regardless of which section is currently selected and
// "All" === sum of the rows. Falls back to counting the loaded page when the breakdown is absent
// (older API) — that legacy path only reflects what is loaded, but keeps the UI functional.
function buildSectionEntries(
  countsBySection: SectionStat[] | undefined,
  loadedItems: WorkItemSummary[],
  status: WorkItemStatus | 'all',
  selectedSectionId: string | undefined,
  sectionTitles: Map<string, string>,
): SectionEntry[] {
  if (countsBySection) {
    return countsBySection
      .map((section) => {
        const count = status === 'all' ? section.total : (section.byStatus[status] ?? 0);
        const label = deriveSectionLabel(section.sampleClusterKey, section.sectionId, sectionTitles);
        return { sectionId: section.sectionId, label, count, icon: sectionIcon(label) };
      })
      // Hide sections with nothing under the current status, but always keep the actively selected
      // section visible (even at 0) so the user can see/clear the filter they are applying.
      .filter((entry) => entry.count > 0 || entry.sectionId === selectedSectionId)
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  const sections = new Map<string, SectionEntry>();
  for (const item of loadedItems) {
    const existing = sections.get(item.sectionId);
    if (existing) {
      existing.count += 1;
      continue;
    }
    const label = deriveSectionLabel(item.clusterKey, item.sectionId, sectionTitles);
    sections.set(item.sectionId, { sectionId: item.sectionId, label, count: 1, icon: sectionIcon(label) });
  }
  return [...sections.values()].sort((left, right) => left.label.localeCompare(right.label));
}

// Applies status transitions to a QueueStats snapshot in-memory, keeping countsByStatus AND
// countsBySection consistent (item totals are unchanged — only the status bucket moves). Used to
// reflect approve/cancel actions in the Section Tree immediately, without a refetch (which could
// read a not-yet-consistent GSI right after the write).
function patchQueueStats(
  stats: QueueStats,
  transitions: Array<{ sectionId: string; from: WorkItemStatus; to: WorkItemStatus }>,
): QueueStats {
  if (transitions.length === 0) {
    return stats;
  }
  const countsByStatus = { ...stats.countsByStatus };
  const sectionMap = new Map(
    (stats.countsBySection ?? []).map((section) => [section.sectionId, { ...section, byStatus: { ...section.byStatus } }]),
  );
  for (const { sectionId, from, to } of transitions) {
    if (from === to) {
      continue;
    }
    if (countsByStatus[from] > 0) {
      countsByStatus[from] -= 1;
    }
    countsByStatus[to] += 1;
    const section = sectionMap.get(sectionId);
    if (section) {
      if (section.byStatus[from] > 0) {
        section.byStatus[from] -= 1;
      }
      section.byStatus[to] += 1;
    }
  }
  return {
    ...stats,
    countsByStatus,
    ...(stats.countsBySection ? { countsBySection: [...sectionMap.values()] } : {}),
  };
}

function deriveSectionLabel(clusterKey: string, sectionId: string, sectionTitles: Map<string, string>) {
  // The course structure is authoritative: sectionId IS the top-level Brain2 group id, so its
  // real title beats any convention parsed out of a clusterKey. Lanes that do not follow the
  // structural `prefix:rest` convention (ELPI practice items use `L1-FSA-ELPI-4608`) previously
  // fell all the way through to a bare "Section 10".
  const title = sectionTitles.get(sectionId);
  if (title) {
    return title;
  }
  // Fallback while the structure is still loading, or when it has no group for this sectionId:
  // a clusterKey of the expected `prefix:rest` shape still yields a name; anything else
  // (no colon, empty prefix) falls back to the stable sectionId so labels never become misleading.
  const prefix = clusterKey.includes(':') ? clusterKey.split(':')[0] : '';
  if (!prefix) {
    return `Section ${sectionId}`;
  }
  return prefix
    .split(/[-_]/g)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

// Flattens the group tree into `id -> title`. Keyed by string because sectionId is a string
// everywhere in the workflow schema. Every depth is indexed, not just the top level: a sectionId
// is *expected* to be a top-level group, but if one ever points deeper, its real title is still a
// better label than the raw id. Iterative so a pathological tree cannot overflow the stack, and
// first-writer-wins so a duplicate id lower in the tree cannot silently rename a section.
function buildGroupTitleIndex(groups: GroupNode[]): Map<string, string> {
  const index = new Map<string, string>();
  const queue: GroupNode[] = [...groups];
  while (queue.length > 0) {
    const node = queue.shift()!;
    const key = String(node.id);
    if (!index.has(key)) {
      index.set(key, node.title);
    }
    if (node.children?.length) {
      queue.push(...node.children);
    }
  }
  return index;
}

function sectionIcon(label: string): string {
  return SECTION_ICONS[label.toLowerCase()] ?? 'folder';
}

interface OpPresentation {
  icon: string; // Material Symbols Outlined glyph
  label: string; // short badge text
  tooltip: string; // hover/aria explanation
}

// Self-explanatory icon + label + tooltip per operation. `create` and `fix` vary by what
// they actually do (new node vs new module; prerequisite rewire vs corrective fix), so this
// takes the whole item rather than just the op string.
function compactRatingLabel(key: string, label: string): string {
  return key === 'academic_accuracy' ? 'Accuracy' : label;
}

// Header pill for a cluster anchored by a create. Task creates (decoupled practice items)
// must never read as new concept-map nodes.
function clusterCreateBadgeLabel(meta: {
  newNodeCount: number;
  newModuleCount: number;
  newPracticeItemCount: number;
}): string {
  if (meta.newNodeCount > 1 || meta.newModuleCount > 0) {
    return 'New concept';
  }
  if (meta.newNodeCount === 0 && meta.newPracticeItemCount > 0) {
    return 'New practice item';
  }
  return 'New node';
}

/**
 * Readiness badge copy for the queue row. `null` for a row whose evidence resolves — a badge on
 * every row carries no information and costs scan time on a 161-item queue.
 */
function evidenceBadgeFor(item: WorkItemSummary): { label: string; tooltip: string } | null {
  if (!evidenceGateApplies(item)) {
    return null;
  }
  switch (evidenceReadiness(item)) {
    case 'none':
      return {
        label: 'no evidence',
        tooltip: 'This item cites no curriculum source — nothing in it can be checked against the reading',
      };
    case 'unresolved':
      return {
        label: 'evidence unavailable',
        // Not "not ingested": `unresolved` also covers an item that cites no curriculum PDF at all.
        tooltip: 'Nothing this item cites can be opened as curriculum evidence',
      };
    default:
      return null;
  }
}

function opPresentation(item: WorkItemSummary): OpPresentation {
  if (isNoSecondTry(item)) return { icon: 'looks_one', label: 'One attempt', tooltip: 'Wrong picks show the full explanation and continue' };
  if (isStepAppend(item)) {
    return { icon: 'playlist_add', label: 'Add steps to lesson', tooltip: 'Add steps and options to an existing lesson' };
  }
  if (item.op === 'create' && item.taskType === 'practice_item' && item.intendedParent?.newLessonWorkItemId) {
    return { icon: 'quiz', label: 'Question for a new lesson', tooltip: 'Practice question for the proposed new lesson' };
  }
  if (item.op === 'create' && item.targetType === 'entity' && item.lessonPlacement) {
    return { icon: 'note_add', label: 'New lesson', tooltip: 'Create a lesson at the recorded placement' };
  }
  switch (item.op) {
    case 'create':
      if (item.targetType === 'group') {
        return { icon: 'create_new_folder', label: 'New module', tooltip: 'Create a new module (group) to hold lessons' };
      }
      if (item.targetType === 'task') {
        return { icon: 'quiz', label: 'New practice item', tooltip: 'Create a new practice item — attaches to its intended lesson once that lesson exists' };
      }
      return { icon: 'note_add', label: 'New node', tooltip: 'Create a new micro-lesson (concept node)' };
    case 'fix':
      return isPrereqRewirePatch(item.suggestedPatch)
        ? { icon: 'account_tree', label: 'Rewire', tooltip: 'Rewire prerequisites — change concept-map edges only (no content change)' }
        : { icon: 'build', label: 'Fix', tooltip: 'Corrective fix to an existing item' };
    case 'rename':
      return { icon: 'drive_file_rename_outline', label: 'Rename', tooltip: 'Rename — change the title only' };
    case 'move':
      return { icon: 'drive_file_move', label: 'Move', tooltip: 'Move to a different parent module' };
    case 'reorder':
      return { icon: 'swap_vert', label: 'Reorder', tooltip: 'Reorder — change the position among siblings' };
    case 'rewrite':
      return { icon: 'edit_document', label: 'Rewrite', tooltip: 'Rewrite the internal content (steps/responses)' };
    case 'augment_practice':
      return { icon: 'quiz', label: 'Practice', tooltip: 'Add or expand practice items' };
    case 'archive':
      return { icon: 'archive', label: 'Archive', tooltip: 'Archive — hide without deleting' };
    default:
      return { icon: 'label', label: item.op, tooltip: `${item.op} — structural change` };
  }
}

function buildBulkFailureMessage(failures: BulkApproveFailure[]) {
  return failures.map((failure) => `Failed to approve ${failure.workItemId}: ${failure.error}`).join(' ');
}

/**
 * A bulk approve now returns a report, not a count. Say what actually happened: a run that
 * re-approved nothing because it had already landed must not read as fresh work, and a run the
 * server stopped early must say so — the operator's next move is to run it again.
 */
function buildBulkApproveSuccessMessage(result: BulkApproveOutcome): string {
  const parts = [`Approved ${result.succeeded.length} items.`];
  if (result.alreadyApproved.length > 0) {
    parts.push(`${result.alreadyApproved.length} were already approved.`);
  }
  return [...parts, ...buildBulkApproveWarnings(result)].join(' ');
}

/**
 * The conditions that must never be reported in a green banner, each phrased as the operator's next
 * move. Kept separate from the count sentence so it can be appended to a failure message too.
 */
function buildBulkApproveWarnings(result: BulkApproveOutcome): string[] {
  const warnings: string[] = [];
  if (result.report.totals.partialGroups > 0) {
    warnings.push(
      `${result.report.totals.partialGroups} atomic group(s) are SPLIT — some members approved and some not. ` +
        'Resolve them before composing a release wave.',
    );
  }
  if (result.trailIncomplete.length > 0) {
    warnings.push(
      `${result.trailIncomplete.length} approval(s) were written but may be missing their review-event or audit row: ` +
        `${result.trailIncomplete.join(', ')}.`,
    );
  }
  if (result.report.stoppedEarly) {
    warnings.push(result.report.stoppedReason ?? 'The run stopped early; re-run to continue.');
  }
  return warnings;
}

function clusterStatusLabel(status: { total: number; pending: number; reviewed: number; cancelled: number }): string {
  if (status.pending === status.total) {
    return `${status.pending} pending`;
  }
  if (status.pending === 0) {
    if (status.reviewed === 0 && status.cancelled > 0) {
      return 'All cancelled';
    }
    return status.cancelled > 0 ? `${status.reviewed} approved · ${status.cancelled} cancelled` : `${status.reviewed} approved`;
  }
  return `${status.pending} pending · ${status.total - status.pending} reviewed`;
}

function clusterStatusChipClass(status: { total: number; pending: number }): string {
  if (status.pending === status.total) {
    return 'rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-600';
  }
  if (status.pending === 0) {
    return 'rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-600';
  }
  return 'rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-600';
}
